// Icons
import BadgeIcon from '@mui/icons-material/Badge'
import CakeIcon from '@mui/icons-material/Cake'
import ContactEmergencyIcon from '@mui/icons-material/ContactEmergency'
// removed: DirectionsBikeIcon not used here after refactor
import DownhillSkiingIcon from '@mui/icons-material/DownhillSkiing'
import EditIcon from '@mui/icons-material/Edit'
import KeyIcon from '@mui/icons-material/Key'
import LogoutIcon from '@mui/icons-material/Logout'
import PersonIcon from '@mui/icons-material/Person'
import PhoneIcon from '@mui/icons-material/Phone'
// removed: PoolIcon not used here after refactor
import SchoolIcon from '@mui/icons-material/School'
// removed: SnowboardingIcon not used here after refactor
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { CircularProgress } from '@mui/material'
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid2 as Grid,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useAtomValue } from 'jotai'
// import removed: MDEditor moved into form component
import md5 from 'md5'
import { useCallback } from 'react'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'

import BioCard from './cards/BioCard'
import PictureManagement from './cards/PictureManagement'
import VideoManagement from './cards/VideoManagement'
import useBar from './common/Bar'
import ImageUploader from './common/ImageUploader'
import UserContactDetail from './common/UserContactDetail'
// removed: FamilyMemberForm moved into UserProfileForm
import UserProfileForm from './forms/UserProfileForm'
import { authDataAtom } from '../domains/user/AtomUsers'
import AuthService from '../services/AuthService'
import ImageService from '../services/ImageService'
// CERTIFICATIONS only used in form component
import { isServerImage } from '../share/utility'
import { avatarUrl } from '../utils/gravatar'
import { SPORTS_OPTIONS, SKILL_LEVEL_TOOLTIP, RELATIONS_OPTIONS, SKILL_LEVELS_OPTIONS } from '../utils/UIConstant'
import { isValidNorthAmericanPhoneNumber, formatNorthAmericanPhoneNumber, toTelHref, isValidWeChatId } from '../utils/utility'

// sportTypes moved into form component

const Item = ({ icon, title, value, children }) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, sm: 2, md: 1, lg: 2 } }}>
      {icon}
      {title && (
        <Typography color="text.secondary" sx={{ textTransform: 'capitalize' }}>
          {title}
        </Typography>
      )}
      {value && <Typography>{value}</Typography>}
      {children && (
        <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
          {children}
        </Stack>
      )}
    </Box>
  )
}

// ContactInfoNotice moved into form component

/**
 * UserProfile - A modern, responsive user profile component that displays user information in a card-based layout.
 * Features include:
 * - Personal info display (name, email, phone)
 * - Bio section
 * - Instructor certificates (if applicable)
 * - Membership details (ID, roles, sports)
 * - Emergency contact information
 * - Profile editing functionality
 * - Logout capability
 *
 * @param {Object} props
 * @param {Object} props.user - User data object containing personal information
 * @param {Function} props.onLogout - Callback function to handle user logout
 * @param {Function} props.onUpdateProfile - Callback function to handle profile updates
 * @param {boolean} [props.defaultOpenEdit=false] - If true, open edit dialog on mount
 * @param {Function} [props.onCloseEdit] - Callback after edit dialog closes to allow parent to cleanup URL state
 * @param {boolean} [props.isLoggingOut=false] - Whether logout is in progress
 */
const UserProfile = ({ user, onLogout, onUpdateProfile, defaultOpenEdit = false, onCloseEdit, isLoggingOut = false }) => {
  const navigate = useNavigate()
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(defaultOpenEdit)
  const [editedUser, setEditedUser] = useState(null)
  const [openAvatarDialog, setOpenAvatarDialog] = React.useState(false)
  const [isDeletingAvatar, setIsDeletingAvatar] = useState(false)
  const [uploadedImageData, setUploadedImageData] = useState(null)
  const [isConfirmingAvatar, setIsConfirmingAvatar] = useState(false)
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [isResetting, setIsResetting] = useState(false)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const bar = useBar()
  const authData = useAtomValue(authDataAtom)
  const getGravatarProfileUrl = () => {
    return `https://gravatar.com/${md5(user.email.toLowerCase().trim())}`
  }

  /**
   * 判断头像是否来自 Gravatar
   * @returns {boolean} 如果是 Gravatar 头像返回 true，否则返回 false
   */
  const isGravatarAvatar = () => {
    // 如果没有自定义头像，则使用 Gravatar
    if (!user.avatar || user.avatar.trim() === '') {
      return true
    }

    // 如果头像 URL 包含 gravatar.com，则是 Gravatar
    if (user.avatar.includes('gravatar.com')) {
      return true
    }

    // 其他情况（服务器上传、Google OAuth 等）都不是 Gravatar
    return false
  }

  useEffect(() => {
    if (isEditDialogOpen) {
      setEditedUser({ ...user })
    } else {
      setEditedUser(null)
    }
  }, [isEditDialogOpen, user])

  const handleAvatarClick = () => {
    setOpenAvatarDialog(true)
  }

  const handleCloseAvatarDialog = () => {
    // 如果关闭对话框时有未确认的上传图片，删除它
    if (uploadedImageData) {
      handleCancelAvatarUpload()
    }
    setOpenAvatarDialog(false)
    setUploadedImageData(null)
  }

  /**
   * 处理头像上传成功（仅保存到状态，不立即更新用户资料）
   */
  const handleAvatarUploadSuccess = (imageData) => {
    // 保存上传的图片数据到状态，等待用户确认
    setUploadedImageData(imageData)
    bar.success('Image uploaded successfully. Please click "Confirm" to update your profile picture.')
  }

  /**
   * 自定义头像上传处理函数（重命名文件为 profile）
   */
  const handleAvatarFileUpload = async (file) => {
    // 使用自定义文件名 "profile" 上传
    return await ImageService.uploadImage(file, 'profile')
  }

  /**
   * 处理确认头像更新
   */
  const handleConfirmAvatarUpdate = async () => {
    if (!uploadedImageData) {
      return
    }

    setIsConfirmingAvatar(true)

    try {
      // 更新用户头像 URL
      if (onUpdateProfile) {
        await onUpdateProfile({
          ...user,
          avatar: uploadedImageData.r2Url,
        })
      }

      bar.success('Profile picture updated successfully')
      setUploadedImageData(null)
      setOpenAvatarDialog(false)
    } catch (error) {
      console.error('Failed to update avatar:', error)
      bar.error(error.message || 'Failed to update profile picture')
    } finally {
      setIsConfirmingAvatar(false)
    }
  }

  /**
   * 处理取消头像上传（删除已上传的图片）
   */
  const handleCancelAvatarUpload = async () => {
    if (!uploadedImageData) {
      return
    }

    try {
      // 删除已上传但未确认的图片
      await ImageService.deleteImage(uploadedImageData.resourceId)
      setUploadedImageData(null)
      bar.info('Uploaded image has been cancelled')
    } catch (error) {
      console.error('Failed to delete uploaded image:', error)
      // 即使删除失败，也清除状态
      setUploadedImageData(null)
    }
  }

  /**
   * 处理删除头像
   */
  const handleDeleteAvatar = async () => {
    if (!user.avatar) {
      return
    }

    // 检查是否是服务器上传的图片
    const isServerImageBool = isServerImage(user.avatar)

    setIsDeletingAvatar(true)

    try {
      // 如果是服务器图片，尝试找到并删除对应的图片
      if (isServerImageBool) {
        try {
          // 通过查询图片列表来找到对应的 resourceId
          // 获取用户的图片列表，查找匹配的图片
          const images = await ImageService.getImages({ ownerId: user.resourceId })
          const matchingImage = images.find((img) => {
            let res = img.r2Url === user.avatar
            return res
          })
          if (matchingImage) {
            console.log('matchingImage: ', matchingImage)
            console.log('matchingImage.resourceId: ', matchingImage.resourceId)
            try {
              await ImageService.deleteImage(matchingImage.resourceId)
            } catch (deleteError) {
              console.warn('Failed to delete image from server:', deleteError)
              // 继续执行，即使删除失败也更新用户信息
            }
          }
        } catch (error) {
          // 如果查找或删除图片失败，继续更新用户信息（移除头像 URL）
          console.warn('Error while trying to delete image from server:', error)
        }
      }

      // 更新用户信息，移除头像 URL
      if (onUpdateProfile) {
        await onUpdateProfile({
          ...user,
          avatar: '',
        })
      }

      bar.success('Profile picture removed successfully')
      setOpenAvatarDialog(false)
    } catch (error) {
      console.error('Failed to delete avatar:', error)
      bar.error(error.message || 'Failed to remove profile picture')
    } finally {
      setIsDeletingAvatar(false)
    }
  }

  const handleEditClick = () => {
    setEditedUser({ ...user })
    setIsEditDialogOpen(true)
  }

  const handleCloseEdit = () => {
    setIsEditDialogOpen(false)
    setEditedUser(null)
    if (onCloseEdit) {
      onCloseEdit()
    }
  }

  const handleSaveEdit = async () => {
    if (onUpdateProfile) {
      try {
        let payload = { ...editedUser }
        const wechat = payload.weChatId || ''
        if (wechat && !isValidWeChatId(wechat)) {
          bar.error('Invalid WeChat ID: 6-20 chars, start with letter, only letters/digits/_/-')
          return
        }
        payload.weChatId = (wechat || '').trim()
        const phone = (payload.phoneNumber || '').trim()
        if (phone && isValidNorthAmericanPhoneNumber(phone)) {
          payload.phoneNumber = formatNorthAmericanPhoneNumber(phone)
        }
        const emergencyPhone = (payload?.emergencyContact?.phone || '').trim()
        if (emergencyPhone && isValidNorthAmericanPhoneNumber(emergencyPhone)) {
          payload.emergencyContact = {
            ...(payload.emergencyContact || {}),
            phone: formatNorthAmericanPhoneNumber(emergencyPhone),
          }
        }
        await onUpdateProfile(payload)
        setIsEditDialogOpen(false)
        setEditedUser(null)
        if (onCloseEdit) {
          onCloseEdit()
        }
      } catch (error) {
        console.error('Failed to update profile:', error)
        bar.error(error.message || 'Failed to update profile')
      }
    }
  }

  const handleInputChange = useCallback(
    (field, value) => {
      setEditedUser((prev) => ({
        ...prev,
        [field]: value,
      }))
    },
    [setEditedUser],
  )

  const handleEmergencyContactChange = useCallback(
    (field, value) => {
      setEditedUser((prev) => ({
        ...prev,
        emergencyContact: {
          ...prev.emergencyContact,
          [field]: value,
        },
      }))
    },
    [setEditedUser],
  )

  const handleFamilyMembersChange = useCallback(
    (familyMembers) => {
      setEditedUser((prev) => ({
        ...prev,
        familyMembers,
      }))
    },
    [setEditedUser],
  )

  const handlePasswordDialogOpen = () => {
    setIsPasswordDialogOpen(true)
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setPasswordError('')
  }

  const handlePasswordDialogClose = () => {
    setIsPasswordDialogOpen(false)
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setPasswordError('')
  }

  const handleResetPassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required')
      return
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New password and confirm password do not match')
      return
    }

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters long')
      return
    }

    setIsResetting(true)
    setPasswordError('')

    try {
      await AuthService.updatePassword({
        currentPassword,
        newPassword,
        token: authData.token,
      })
      handlePasswordDialogClose()
      bar.success('Password has been successfully updated')
    } catch (error) {
      if (error.response) {
        setPasswordError(error.response.data.message || 'Failed to reset password')
      } else {
        setPasswordError('Network error. Please try again')
      }
    } finally {
      setIsResetting(false)
    }
  }

  /**
   * Handle pictures change (add/delete)
   */
  const handlePicturesChange = async (updatedPictures) => {
    if (onUpdateProfile) {
      await onUpdateProfile({
        ...user,
        pictures: updatedPictures,
      })
    }
  }

  /**
   * Handle videos change (add/delete)
   */
  const handleVideosChange = async (updatedVideos) => {
    if (onUpdateProfile) {
      await onUpdateProfile({
        ...user,
        videos: updatedVideos,
      })
    }
  }

  // Add null checks for arrays
  const roles = user?.roles || []
  const sports = user?.sports || []
  const certificates = user?.instructorCertificates || []

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 1, sm: 2 }, width: '100%' }}>
      {/* Header Section, including Avatar, Name, Email, Phone, WeChat ID, and Edit Button */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid
            container
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 2, sm: 3 }}
            sx={{
              alignItems: 'center', // Align items horizontally
              justifyContent: 'flex-start', // Left to right alignment
              flexWrap: 'nowrap', // All items in a single row, no wrapping
              pl: { xs: 0, sm: 3, md: 3, lg: 8 },
              pr: { xs: 0, sm: 0, md: 2, lg: 2 },
            }}
          >
            {/* Avatar with Name Section */}
            <Grid
              container
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{
                alignItems: 'center', // Align items horizontally
                justifyContent: 'flex-start', // Left to right alignment
              }}
            >
              <Grid
                sx={{
                  position: 'relative',
                  cursor: 'pointer',
                  width: { xs: 140, sm: 140 },
                  height: { xs: 140, sm: 140 },
                  '&:hover .edit-overlay': {
                    opacity: 1,
                  },
                }}
                onClick={handleAvatarClick}
              >
                <Avatar
                  src={avatarUrl(user, 240)}
                  sx={{
                    width: { xs: 140, sm: 140 },
                    height: { xs: 140, sm: 140 },
                    bgcolor: 'primary.main',
                    transition: 'opacity 0.2s ease-in-out',
                  }}
                >
                  {/* {user.firstName?.charAt(0)} */}
                </Avatar>
                {/* Edit overlay on hover */}
                <Box
                  className="edit-overlay"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: { xs: 140, sm: 140 },
                    height: { xs: 140, sm: 140 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    borderRadius: '50%',
                    opacity: 0,
                    transition: 'opacity 0.2s ease-in-out',
                    pointerEvents: 'none',
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'white',
                      fontSize: '1.2rem',
                      fontWeight: 'bold',
                    }}
                  >
                    Edit
                  </Typography>
                </Box>
              </Grid>

              {/* Name Section and Button Section */}
              <Grid
                sx={{
                  textAlign: { xs: 'center', sm: 'left' },
                }}
              >
                {/* Name Section and Email Section */}
                <Box>
                  <Typography
                    variant="h4"
                    gutterBottom
                    sx={{
                      fontSize: { xs: '1.5rem', sm: '2rem' },
                    }}
                  >
                    {user.firstName} {user.lastName}
                  </Typography>

                  <UserContactDetail user={user} />
                </Box>
              </Grid>
            </Grid>

            {/* Button Section */}
            <Grid size={{ xs: 12, sm: 'auto' }} sx={{ ml: { xs: 0, sm: 'auto' } }}>
              <Stack
                direction={{ xs: 'row', sm: 'column' }}
                justifyContent={{ xs: 'center', sm: 'flex-start' }}
                sx={{
                  flexWrap: 'wrap',
                  // Add space between lines when wrap
                  gap: { xs: 1 },
                  mt: { xs: 2, sm: 0 },
                  width: { xs: '100%', md: 'auto' },
                }}
              >
                <Button variant="outlined" startIcon={<EditIcon />} onClick={handleEditClick} size="small" sx={{ fontSize: { sm: '0.875rem' }, whiteSpace: 'nowrap' }}>
                  Edit
                </Button>
                {user.roles?.includes('INSTRUCTOR') && (
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<PersonIcon />}
                    onClick={() => navigate(`/instructors/${user.resourceId}`)}
                    size="small"
                    sx={{ fontSize: { sm: '0.875rem' }, whiteSpace: 'nowrap' }}
                  >
                    View Instructor Profile
                  </Button>
                )}
                <Button variant="outlined" startIcon={<KeyIcon />} onClick={handlePasswordDialogOpen} size="small" sx={{ fontSize: { sm: '0.875rem' }, whiteSpace: 'nowrap' }}>
                  Change Password
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={isLoggingOut ? <CircularProgress size={16} color="inherit" /> : <LogoutIcon />}
                  onClick={onLogout}
                  disabled={isLoggingOut}
                  size="small"
                  sx={{
                    fontSize: { sm: '0.875rem' },
                    whiteSpace: 'nowrap',
                  }}
                >
                  {isLoggingOut ? 'Logging out...' : 'Logout'}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Main Content Grid, inlcuding Bio, Family Members, and Instructor Profile */}
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {/* Left Column */}
        <Grid size={{ xs: 12, sm: 12, md: 8 }}>
          {/* Bio Section */}
          <Card sx={{ mb: { xs: 2, sm: 3 } }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                About Me
              </Typography>
              <Divider sx={{ my: { xs: 1, sm: 2 } }} />
              <BioCard bio={user.bio} />
            </CardContent>
          </Card>
          {/* Family Members - Only show if user has family members */}
          {user.familyMembers && user.familyMembers.length > 0 && (
            <Card sx={{ mb: { xs: 2, sm: 3 } }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Family Members
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Stack spacing={3}>
                  {user.familyMembers.map((member, index) => {
                    // Map relationship values to display labels
                    const relationshipLabel = RELATIONS_OPTIONS[member.relation]?.label

                    return (
                      <Box key={member.familyId || index} sx={{ pb: 1, borderBottom: index < user.familyMembers.length - 1 ? '1px dashed' : 'none', borderColor: 'divider' }}>
                        <Typography variant="body1" fontWeight="bold" gutterBottom>
                          {member.name}
                        </Typography>
                        <Stack spacing={2} sx={{ mb: 1, px: { xs: 0, sm: 3, md: 6, lg: 8 } }}>
                          {relationshipLabel && <Item icon={<BadgeIcon fontSize="small" />} value={relationshipLabel} />}
                          {member.age && <Item icon={<CakeIcon fontSize="small" />} title="Age" value={member.age} />}
                          {member.phoneNumber && <Item icon={<PhoneIcon fontSize="small" />} title="Phone" value={member.phoneNumber} />}
                          {member.sports && member.sports.length > 0 && (
                            <Stack spacing={0.75} sx={{ width: '100%' }}>
                              {member.sports.map((sport, sportIndex) => {
                                const { icon, label } = SPORTS_OPTIONS[sport.name]
                                const skillLabel = (SKILL_LEVEL_TOOLTIP[sport.name] && SKILL_LEVEL_TOOLTIP[sport.name][sport.skillLevel]?.label) || sport.skillLevel || ''
                                return (
                                  <Box key={sportIndex} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>{icon}</Box>
                                    <Chip label={label || sport.name} size="small" variant="outlined" />
                                    <Chip label={skillLabel} size="small" color={SKILL_LEVELS_OPTIONS[sport.skillLevel]?.color} variant="outlined" />
                                  </Box>
                                )
                              })}
                            </Stack>
                          )}
                        </Stack>
                      </Box>
                    )
                  })}
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* Picture Gallery */}
          <Card sx={{ mb: { xs: 2, sm: 3 } }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Public Photo Gallery
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <PictureManagement pictures={user.pictures || []} onPicturesChange={handlePicturesChange} editable={true} />
            </CardContent>
          </Card>

          {/* Video Gallery */}
          <Card sx={{ mb: { xs: 2, sm: 3 } }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Public Video Gallery
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <VideoManagement videos={user.videos || []} onVideosChange={handleVideosChange} editable={true} />
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column */}
        <Grid size={{ xs: 12, sm: 12, md: 4 }}>
          {/* Membership Info */}
          <Card sx={{ mb: { xs: 2, sm: 3 } }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Membership
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Stack spacing={2}>
                {/* Member ID */}
                <Item icon={<BadgeIcon fontSize="small" />} title="Member ID" value={user.clubMembershipId} />
              </Stack>
            </CardContent>
          </Card>

          {/* Account Info */}
          <Card sx={{ mb: { xs: 2, sm: 3 } }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Account
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Stack spacing={2}>
                <Item key={'UserId'} icon={<BadgeIcon fontSize="small" />} title="Account ID" value={user.resourceId} />

                <Item
                  key={'roles'}
                  icon={<SchoolIcon fontSize="small" />}
                  title="Roles"
                  children={roles.map((role, index) => (
                    <Chip key={role + index} label={role} size="small" color="default" variant="outlined" />
                  ))}
                />

                <Item
                  key={'sports'}
                  icon={<DownhillSkiingIcon fontSize="small" />}
                  title="Sports"
                  children={sports.map((sport, index) => (
                    <Chip key={index} label={sport} size="small" color="default" variant="outlined" />
                  ))}
                />

                {user.roles?.includes('INSTRUCTOR') && (
                  <Item
                    key={'certificates'}
                    icon={<SchoolIcon fontSize="small" />}
                    title="Certificates"
                    children={certificates.map((cert) => (
                      <Chip key={cert} label={cert} size="small" color="primary" variant="outlined" />
                    ))}
                  />
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Emergency Contact */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Emergency Contact
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Stack spacing={2}>
                <Item icon={<ContactEmergencyIcon fontSize="small" />} value={user?.emergencyContact?.name || ''} />
                <Item
                  icon={<PhoneIcon fontSize="small" />}
                  children={
                    user?.emergencyContact?.phone ? (
                      <Link href={toTelHref(user.emergencyContact.phone)} underline="hover">
                        {user.emergencyContact.phone}
                      </Link>
                    ) : null
                  }
                />
                <Item icon={<BadgeIcon fontSize="small" />} value={user?.emergencyContact?.relationship || ''} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Dialog */}
      <Dialog
        open={isEditDialogOpen}
        onClose={handleCloseEdit}
        maxWidth="md"
        fullWidth
        fullScreen={window.innerWidth < 600}
        disableEnforceFocus
        disableRestoreFocus
        keepMounted={false}
        aria-labelledby="edit-profile-dialog-title"
      >
        <DialogTitle
          id="edit-profile-dialog-title"
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 2 },
          }}
        >
          Edit Profile
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          {editedUser ? (
            <UserProfileForm
              value={editedUser}
              onInputChange={handleInputChange}
              onEmergencyContactChange={handleEmergencyContactChange}
              onFamilyMembersChange={handleFamilyMembersChange}
            />
          ) : (
            <CircularProgress />
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 2 },
          }}
        >
          <Button onClick={handleCloseEdit} tabIndex={0}>
            Cancel
          </Button>
          <Button onClick={handleSaveEdit} variant="contained" tabIndex={0}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={isPasswordDialogOpen} onClose={handlePasswordDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          {passwordError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {passwordError}
            </Typography>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Current Password"
              type={showCurrentPassword ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              disabled={isResetting}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <KeyIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {showCurrentPassword ? (
                      <VisibilityOffIcon
                        sx={{
                          cursor: 'pointer',
                          color: 'action.active',
                          '&:hover': {
                            color: 'primary.main',
                          },
                        }}
                        onClick={() => setShowCurrentPassword(false)}
                      />
                    ) : (
                      <VisibilityIcon
                        sx={{
                          cursor: 'pointer',
                          color: 'action.active',
                          '&:hover': {
                            color: 'primary.main',
                          },
                        }}
                        onClick={() => setShowCurrentPassword(true)}
                      />
                    )}
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="New Password"
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              disabled={isResetting}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <KeyIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {showNewPassword ? (
                      <VisibilityOffIcon
                        sx={{
                          cursor: 'pointer',
                          color: 'action.active',
                          '&:hover': {
                            color: 'primary.main',
                          },
                        }}
                        onClick={() => setShowNewPassword(false)}
                      />
                    ) : (
                      <VisibilityIcon
                        sx={{
                          cursor: 'pointer',
                          color: 'action.active',
                          '&:hover': {
                            color: 'primary.main',
                          },
                        }}
                        onClick={() => setShowNewPassword(true)}
                      />
                    )}
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={isResetting}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <KeyIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {showConfirmPassword ? (
                      <VisibilityOffIcon
                        sx={{
                          cursor: 'pointer',
                          color: 'action.active',
                          '&:hover': {
                            color: 'primary.main',
                          },
                        }}
                        onClick={() => setShowConfirmPassword(false)}
                      />
                    ) : (
                      <VisibilityIcon
                        sx={{
                          cursor: 'pointer',
                          color: 'action.active',
                          '&:hover': {
                            color: 'primary.main',
                          },
                        }}
                        onClick={() => setShowConfirmPassword(true)}
                      />
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePasswordDialogClose} disabled={isResetting}>
            Cancel
          </Button>
          <Button onClick={handleResetPassword} variant="contained" disabled={isResetting}>
            {isResetting ? 'Resetting...' : 'Reset Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Avatar Dialog */}
      <Dialog open={openAvatarDialog} onClose={handleCloseAvatarDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Profile Picture</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* 当前头像预览 */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Avatar src={uploadedImageData ? uploadedImageData.r2Url : avatarUrl(user)} sx={{ width: 120, height: 120 }}>
                {user.firstName?.charAt(0)}
              </Avatar>
              <Typography variant="body2" color="text.secondary" align="center">
                {uploadedImageData ? 'Preview of your new profile picture' : user.avatar ? 'You have a custom profile picture' : 'Your profile picture is managed through Gravatar'}
              </Typography>
            </Box>

            <Divider />

            {/* 上传新头像 */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Upload New Picture
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Drag and drop an image here, or click to select. Supported formats: JPEG, PNG, GIF, WEBP (max 10MB)
              </Typography>
              {uploadedImageData ? (
                <Box
                  sx={{
                    border: '2px solid',
                    borderColor: 'primary.main',
                    borderRadius: 2,
                    p: 2,
                    textAlign: 'center',
                    bgcolor: 'action.hover',
                  }}
                >
                  <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
                    ✓ Image uploaded successfully
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Click "Confirm" below to update your profile picture, or "Cancel" to remove this image.
                  </Typography>
                </Box>
              ) : (
                <ImageUploader
                  onUploadSuccess={handleAvatarUploadSuccess}
                  customUploadHandler={handleAvatarFileUpload}
                  buttonText="Select Image"
                  dragText="Drag and drop your profile picture here, or click to select"
                  showPreview={true}
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  maxSize={10 * 1024 * 1024}
                  sx={{ mb: 2 }}
                />
              )}
            </Box>

            <Divider />

            {/* 删除当前头像（如果不是 Gravatar 头像） */}
            {!isGravatarAvatar() && (
              <>
                <Box>
                  <Typography variant="subtitle2" gutterBottom color="error">
                    Remove Current Picture
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Remove your custom profile picture and use Gravatar instead.
                  </Typography>
                  <Button variant="outlined" color="error" onClick={handleDeleteAvatar} disabled={isDeletingAvatar} size="small">
                    {isDeletingAvatar ? 'Removing...' : 'Remove Picture'}
                  </Button>
                </Box>
                <Divider />
              </>
            )}

            {/* Gravatar 信息 */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Using Profile Picture from Gravatar
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Gravatar is a service that lets you use the same profile picture across many websites. Your Gravatar is currently linked to:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 2 }}>
                {user.email}
              </Typography>
              <Button variant="outlined" component={Link} href={getGravatarProfileUrl()} target="_blank" rel="noopener noreferrer" size="small">
                Edit on Gravatar
              </Button>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          {uploadedImageData ? (
            <>
              <Button onClick={handleCancelAvatarUpload} disabled={isConfirmingAvatar}>
                Cancel
              </Button>
              <Button onClick={handleConfirmAvatarUpdate} variant="contained" disabled={isConfirmingAvatar}>
                {isConfirmingAvatar ? 'Updating...' : 'Confirm'}
              </Button>
            </>
          ) : (
            <Button onClick={handleCloseAvatarDialog}>Close</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default UserProfile
